import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Select,
    TextField,
    MenuItem,
    FormControl,
    FormControlLabel,
    InputLabel,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    ListItemText,
    OutlinedInput,
    FormGroup
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import ApiHelper from 'utils/apiHelper';
import EditGroupPermissionDialog from './EditGroupPermissionDialog';

const EditPermissionGroupDialog = ({ data, usersData, onClose, onCloseCancel }) => {
    const [loading, setLoading] = useState(true);
    const [groupName, setGroupName] = useState(data.name);
    const [description, setDescription] = useState(data.description);
    const [rootAdmin, setRootAdmin] = useState(data.root_admin);
    const [serverAdmin, setServerAdmin] = useState(data.server_admin);
    const [orgAdmin, setOrgAdmin] = useState(data.org_admin);
    const [groupAdmin, setGroupAdmin] = useState(data.group_admin);
    const [userAdmin, setUserAdmin] = useState(data.user_admin);
    const [selectedUsers, setSelectedUsers] = useState(data.users);
    const [showEditGroupPermissions, setShowEditGroupPermissions] = useState(false);
    const [groupPermissionsData, setGroupPermissionsData] = useState([]);

    useEffect(() => {
        getGroupPermissionsData();
    }, []);

    const getGroupPermissionsData = () => {
        ApiHelper.get(`/api/management/group_permission/${data.id}/`)
            .then((res) => {
                setGroupPermissionsData(res.data);
                setLoading(false);
            })
            .catch((err) => {
                toast.error(`Fail! ${err.response.data.message}`);
                setLoading(false);
            });
    };

    const handleChange = (event) => {
        const {
            target: { value }
        } = event;
        setSelectedUsers(typeof value === 'string' ? value.split(',') : value);
    };

    const onSave = (data) => {
        setGroupPermissionsData(data);
    };

    const handleClickOrgAdmin = (e) => {
        setOrgAdmin(e.target.checked);
        setUserAdmin(true);
        setGroupAdmin(true);
    };

    const handleClickUserAdmin = (e) => {
        setUserAdmin(e.target.checked);
        setGroupAdmin(true);
    };

    const handleSubmit = () => {
        ApiHelper.put(`/api/management/permission_group/${data.id}/`, {
            group_name: groupName,
            group_description: description,
            root_admin: rootAdmin,
            server_admin: serverAdmin,
            org_admin: orgAdmin,
            group_admin: groupAdmin,
            user_admin: userAdmin,
            users: selectedUsers,
            group_permissions: groupPermissionsData
        })
            .then(() => {
                toast.success('Successfully updated');
                onClose();
            })
            .catch(() => {
                toast.error('Failed to update the permission group');
                onCloseCancel();
            });
    };

    return (
        <Dialog open={true} onClose={onCloseCancel}>
            <DialogTitle sx={{ fontSize: '16px' }}>Edit Permission Group</DialogTitle>
            <DialogContent sx={{ minWidth: '500px' }}>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <TextField
                        sx={{
                            width: '100%'
                        }}
                        label="Group Name"
                        value={groupName}
                        disabled={groupName === 'Organization Administration'}
                        onChange={(e) => setGroupName(e.target.value)}
                    />
                </Box>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <TextField
                        sx={{ width: '100%' }}
                        label="Group Description"
                        value={description}
                        disabled={groupName === 'Organization Administration'}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </Box>
                <Box>
                    <FormGroup aria-label="position" row>
                        <FormControlLabel
                            value="top"
                            control={<Checkbox disabled={true} checked={rootAdmin} onChange={(e) => setRootAdmin(e.target.checked)} />}
                            label="Root Admin"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox disabled={true} checked={serverAdmin} onChange={(e) => setServerAdmin(e.target.checked)} />}
                            label="Server Admin"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={
                                <Checkbox
                                    disabled={groupName === 'Organization Administration'}
                                    checked={orgAdmin}
                                    onChange={handleClickOrgAdmin}
                                />
                            }
                            label="Org Admin"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={
                                <Checkbox
                                    disabled={groupName === 'Organization Administration'}
                                    checked={userAdmin}
                                    onChange={handleClickUserAdmin}
                                />
                            }
                            label="User Admin"
                            labelPlacement="top"
                        />
                        <FormControlLabel
                            value="top"
                            control={
                                <Checkbox
                                    disabled={groupName === 'Organization Administration'}
                                    checked={groupAdmin}
                                    onChange={(e) => setGroupAdmin(e.target.checked)}
                                />
                            }
                            label="Group Admin"
                            labelPlacement="top"
                        />
                    </FormGroup>
                </Box>
                <Box>
                    <FormControl sx={{ width: '100%', mt: 2, maxWidth: '550px' }}>
                        <InputLabel>Choose Users</InputLabel>
                        <Select
                            labelId="demo-multiple-checkbox-label"
                            id="demo-multiple-checkbox"
                            multiple
                            value={selectedUsers}
                            onChange={handleChange}
                            input={<OutlinedInput label="Choose Users" />}
                            renderValue={(selected) => selected.join(', ')}
                        >
                            {usersData.map((user) => (
                                <MenuItem key={user.id} value={user.username}>
                                    <Checkbox checked={selectedUsers.indexOf(user.username) > -1} />
                                    <ListItemText primary={user.username} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ mt: 2 }}>
                    <LoadingButton
                        color="primary"
                        variant="contained"
                        loading={loading}
                        startIcon={<Edit />}
                        disabled={groupName === 'Organization Administration'}
                        onClick={() => setShowEditGroupPermissions(true)}
                    >
                        <span>Edit Group Permissions</span>
                    </LoadingButton>
                    {showEditGroupPermissions && (
                        <EditGroupPermissionDialog
                            data={groupPermissionsData}
                            onSave={onSave}
                            onClose={() => setShowEditGroupPermissions(false)}
                        />
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseCancel}>Cancel</Button>
                <Button onClick={handleSubmit}>Submit</Button>
            </DialogActions>
        </Dialog>
    );
};

EditPermissionGroupDialog.propTypes = {
    data: PropTypes.object,
    usersData: PropTypes.array,
    onClose: PropTypes.func,
    onCloseCancel: PropTypes.func
};

export default EditPermissionGroupDialog;
